// Spinner + LoadingState — the single, uniform "work in progress" indicator
// used across every list/detail surface that fetches a non-trivial volume of
// data (precedents, ingestion queue, analytics, …). Before this, screens hand-
// rolled a plain "Loading…" line, which reads as static text on a slow fetch
// and leaves the user unsure whether the page is stuck. Spinner is the bare
// animated ring; LoadingState pairs it with a label inside a card.
//
// Styles live in ds-primitives.css (design-system tokens only).
export interface SpinnerProps {
  /** Diameter in px. Defaults to 16 (inline-with-text size). */
  size?: number;
  /** Accessible label announced to screen readers. */
  label?: string;
  className?: string;
}

export function Spinner({
  size = 16,
  label = "Loading",
  className,
}: SpinnerProps): JSX.Element {
  return (
    <span
      className={className ? `ds-spinner ${className}` : "ds-spinner"}
      style={{ width: size, height: size }}
      role="status"
      aria-label={label}
      data-testid="spinner"
    />
  );
}

export interface LoadingStateProps {
  /** Message shown beside the spinner. */
  label?: string;
  className?: string;
}

// Card-style loading block for "the list is loading" empty space. Polite live
// region so assistive tech announces the wait without stealing focus.
export function LoadingState({
  label = "Loading…",
  className,
}: LoadingStateProps): JSX.Element {
  return (
    <div
      className={className ? `ds-loading ${className}` : "ds-loading"}
      role="status"
      aria-live="polite"
      data-testid="loading-state"
    >
      <Spinner size={18} label={label} />
      <span className="ds-loading__label">{label}</span>
    </div>
  );
}
